import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { TOOLBAR_CONTROL } from '@/components/ui/pageChrome';
import type { ResourceGroupRow, ResourceRow } from '@/hooks/useSchedulingResources';
import { cn } from '@/utils/cn';
import {
  resourceFilterMatches,
  toggleResourceGroupSelection,
  toggleResourceSelection,
} from '@/utils/appointmentResourceCalendar';

interface CalendarResourceFilterProps {
  groups: ResourceGroupRow[];
  resources: ResourceRow[];
  selectedIds: string[] | null;
  onChange: (next: string[] | null) => void;
}

export function CalendarResourceFilter({
  groups,
  resources,
  selectedIds,
  onChange,
}: CalendarResourceFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const activeResources = useMemo(
    () => resources.filter((resource) => resource.is_active),
    [resources],
  );
  const allActiveIds = useMemo(
    () => activeResources.map((resource) => resource.id),
    [activeResources],
  );
  const selectedCount = selectedIds == null ? allActiveIds.length : selectedIds.length;

  const grouped = useMemo(() => {
    const byGroup = new Map<string, ResourceRow[]>();
    for (const resource of activeResources) {
      const list = byGroup.get(resource.resource_group_id) ?? [];
      list.push(resource);
      byGroup.set(resource.resource_group_id, list);
    }
    return groups
      .filter((group) => (byGroup.get(group.id) ?? []).length > 0)
      .map((group) => ({
        group,
        resources: (byGroup.get(group.id) ?? []).filter((resource) =>
          resourceFilterMatches(resource.name, group.name, query),
        ),
      }))
      .filter((entry) => entry.resources.length > 0);
  }, [activeResources, groups, query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={cn(TOOLBAR_CONTROL, 'inline-flex min-w-[10.5rem] items-center justify-between gap-2 px-3')}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid="calendar-resource-filter"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="truncate">
          {selectedIds == null
            ? t('appointments.resource_filter.all')
            : t('appointments.resource_filter.selected', { count: selectedCount })}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ds-muted" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-72 rounded-pop border border-ds-line bg-ds-card p-s3 shadow-pop">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('appointments.resource_filter.search')}
            className={cn(TOOLBAR_CONTROL, 'mb-2 w-full')}
            data-testid="calendar-resource-filter-search"
          />
          <button
            type="button"
            className="mb-2 text-left text-sm font-medium text-ds-teal hover:underline"
            onClick={() => onChange(null)}
          >
            {t('appointments.resource_filter.all')}
          </button>
          <div className="max-h-72 space-y-3 overflow-auto">
            {grouped.length === 0 ? (
              <p className="text-sm text-ds-muted">{t('appointments.resource_filter.empty')}</p>
            ) : (
              grouped.map(({ group, resources: groupResources }) => {
                const groupIds = activeResources
                  .filter((resource) => resource.resource_group_id === group.id)
                  .map((resource) => resource.id);
                const selectedSet = new Set(selectedIds ?? allActiveIds);
                const groupChecked = groupIds.every((id) => selectedSet.has(id));
                return (
                  <div key={group.id}>
                    <label className="flex items-center gap-2 text-sm font-medium text-ds-text">
                      <input
                        type="checkbox"
                        checked={groupChecked}
                        onChange={() =>
                          onChange(toggleResourceGroupSelection(selectedIds, groupIds, allActiveIds))
                        }
                      />
                      {group.name}
                    </label>
                    <div className="mt-1 space-y-1 pl-5">
                      {groupResources.map((resource) => (
                        <label
                          key={resource.id}
                          className="flex items-center gap-2 text-sm text-ds-text"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSet.has(resource.id)}
                            onChange={() =>
                              onChange(
                                toggleResourceSelection(selectedIds, resource.id, allActiveIds),
                              )
                            }
                          />
                          {resource.name}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
